import type { WelfareFishCandidate } from '@scaleaq/sitecontrol-lib';

import { FlexAligner, Typography } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { responsive } from '@/utils';

import {
  formatFilterLabels,
  localizeObservationBreakdown,
  unassessedFilterClasses,
} from '../fishObservationGallery.functions.ts';
import type { OrbitOneGalleryFilterClass } from '../fishObservationGallery.types.ts';
import { ObservationBreakdownBadges } from '../ObservationBreakdownBadges.tsx';

type ObservationBreakdownLineProps = {
  label: string;
  observation: WelfareFishCandidate;
  filterClasses: OrbitOneGalleryFilterClass[];
};

/**
 * Caption line for one observation group. Separates "no detections" (assessed, empty result) from
 * "not assessed" (no packet), since images are only sampled for some welfare types.
 */
export const ObservationBreakdownLine = ({
  label,
  observation,
  filterClasses,
}: ObservationBreakdownLineProps) => {
  const { t } = useTranslation('orbitOne');

  const unassessed = unassessedFilterClasses({ observation, filterClasses });
  const isUnassessed = unassessed.length === filterClasses.length;
  const breakdown = isUnassessed
    ? []
    : localizeObservationBreakdown({
        observation,
        t,
        availableFilterClasses: filterClasses,
      });

  return (
    <FlexAligner alignment='center-left' gap={responsive.spacing.medium} wrap>
      <Typography type='body_xlarge' color='inherit'>
        {label}
      </Typography>
      {isUnassessed && (
        <Typography type='body_xlarge' color='inherit'>
          {t('liceCount.imageGallery.metadata.observationBreakdown.notAssessed')}
        </Typography>
      )}
      {!isUnassessed && breakdown.length === 0 && (
        <Typography type='body_xlarge' color='inherit'>
          {t('liceCount.imageGallery.metadata.observationBreakdown.none')}
        </Typography>
      )}
      {breakdown.length > 0 && <ObservationBreakdownBadges entries={breakdown} size='large' />}
      {!isUnassessed && unassessed.length > 0 && (
        <Typography color='inherit'>
          {t('liceCount.imageGallery.metadata.observationBreakdown.notAssessedFilters', {
            filters: formatFilterLabels({
              filters: unassessed,
              labelForClass: filterClass =>
                t(`liceCount.imageGallery.filterClasses.${filterClass}`),
            }),
          })}
        </Typography>
      )}
    </FlexAligner>
  );
};
